import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.scss';
import clsx from 'clsx';

const Button = forwardRef(function Button({
    variant = 'primary',
    size = 'md',
    className,
    children,
    to,
    href,
    fullWidth = false,
    loading = false,
    disabled,
    type = 'button',
    onClick,
    tabIndex,
    ...props
}, ref) {
    const unavailable = disabled || loading;
    const handleClick = event => {
        if (unavailable) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        onClick?.(event);
    };
    // Generate class names based on props
    const variantClass = styles[`btn--${variant}`] || styles['btn--primary'];
    const sizeClass = styles[`btn--${size}`] || (size === 'sm' ? styles['btn--sm'] : '');

    const combinedClasses = clsx(
        styles.btn,
        variantClass,
        sizeClass,
        fullWidth && styles['btn--full'],
        loading && styles['btn--loading'],
        className
    );
    const content = loading ? (
        <>
            <span className={styles.spinner} aria-hidden="true" />
            {children}
        </>
    ) : children;

    // Removing destinations also blocks auxiliary clicks and context-menu navigation.
    if ((to || href) && unavailable) {
        return <a {...props} ref={ref} role="link" className={combinedClasses}
            aria-disabled="true" aria-busy={loading || undefined} tabIndex={-1} onClick={handleClick}>{content}</a>;
    }
    // Render as React Router Link if 'to' prop is present
    if (to) {
        return (
            <Link {...props} ref={ref} to={to} className={combinedClasses} tabIndex={tabIndex} onClick={handleClick}>
                {content}
            </Link>
        );
    }

    // Render as anchor tag if 'href' prop is present
    if (href) {
        return (
            <a rel={props.target === '_blank' ? 'noopener noreferrer' : undefined} {...props} ref={ref} href={href} className={combinedClasses} tabIndex={tabIndex} onClick={handleClick}>
                {content}
            </a>
        );
    }

    // Default to button element
    return (
        <button {...props} ref={ref} type={type} className={combinedClasses} disabled={unavailable} aria-busy={loading || undefined} tabIndex={tabIndex} onClick={handleClick}>
            {content}
        </button>
    );
});

export default Button;
